import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, SealCheck, MapPin } from '@phosphor-icons/react';
import { projectTypeMeta } from './projectTypeMeta';

// Presentation only. Nothing here changes a stored status, a figure or a calculation - it decides
// which of the five public states a project reads as, and what each state is allowed to show.
const EXECUTION_STATUSES = ['in_execution', 'operating', 'exiting', 'funded'];

export const CARD_STATES = {
  coming_soon: { label: 'Coming soon', className: 'bg-amber-500 text-white', cta: 'View preview' },
  open: { label: 'Open for investment', className: 'bg-primary text-white', cta: 'Invest now' },
  fully_funded: { label: 'Fully funded', className: 'bg-primary text-white', cta: 'View opportunity' },
  active: { label: 'Active', className: 'bg-[rgb(34,150,94)] text-white', cta: 'View progress' },
  exited: { label: 'Exited', className: 'bg-[rgb(var(--foreground))] text-white', cta: 'View case study' },
};

export const resolveCardState = (project) => {
  const lifecycle = project.lifecycle || (project.public_status === 'closed' ? 'exited' : 'open');
  if (lifecycle === 'exited') return 'exited';
  if (lifecycle === 'coming_soon') return 'coming_soon';

  const status = (project.status || '').toLowerCase();
  const progress = Number(project.funding_progress);
  // A project in execution is no longer raising, whatever its funding bar says
  if (EXECUTION_STATUSES.includes(status) && status !== 'funded') return 'active';
  if (status === 'funded' || (Number.isFinite(progress) && progress >= 100)) return 'fully_funded';
  return 'open';
};

// A missing figure is not a zero. Nothing is ever padded with 0 or a dash.
const PENDING = 'TBA';

const compactMoney = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000000) return `€${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `€${Math.round(n / 1000)}K`;
  return `€${n.toLocaleString('en-GB')}`;
};

const exactMoney = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(n);
};

const percent = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${n.toFixed(1)}%` : null;
};

const monthsLabel = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${n} mo` : null;
};

export const Metric = ({ value, label, testId, verified = false }) => {
  const pending = !value;
  return (
    <div className="min-w-0" data-testid={testId}>
      <p
        className={`flex items-center gap-1 text-xl md:text-2xl font-heading font-semibold tracking-tight leading-none ${
          pending ? 'text-foreground-muted/60' : ''
        }`}
      >
        <span className="truncate">{value || PENDING}</span>
        {verified && !pending && (
          <SealCheck
            size={17}
            weight="fill"
            className="flex-shrink-0 text-primary"
            aria-label="Verified result"
            data-testid="card-verified-badge"
          />
        )}
      </p>
      <p className="text-[10px] sm:text-[11px] text-foreground-muted mt-1.5 leading-tight whitespace-nowrap">
        {label}
      </p>
    </div>
  );
};

export const FundingProgress = ({ progress, raised }) => {
  const pct = Math.min(Math.max(Number(progress) || 0, 0), 100);
  return (
    <div data-testid="card-funding-progress">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-medium">
          {pct >= 100 ? 'Fully funded' : `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}% funded`}
        </p>
        {compactMoney(raised) ? (
          <p className="text-xs text-foreground-muted">{compactMoney(raised)} raised</p>
        ) : null}
      </div>
      <div className="h-1.5 w-full rounded-full bg-black/[0.07] overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const metricsFor = (state, project) => {
  const perf = project.performance;
  if (state === 'exited' || project.exit_stage === 'exiting') {
    return [
      {
        value: percent(perf?.actual_return_pct),
        label: 'Actual return',
        testId: 'card-metric-return',
        verified: perf?.actual_return_pct != null,
      },
      { value: monthsLabel(perf?.actual_hold_months), label: 'Actual hold', testId: 'card-metric-hold' },
      {
        value: compactMoney(perf?.distributed ?? perf?.capital_returned ?? perf?.investor_distribution),
        label: 'Distributed',
        testId: 'card-metric-minimum',
      },
    ];
  }
  return [
    { value: percent(project.projected_return), label: 'Projected IRR', testId: 'card-metric-return' },
    { value: monthsLabel(project.target_hold_months), label: 'Target hold', testId: 'card-metric-hold' },
    { value: exactMoney(project.min_investment), label: 'Minimum', testId: 'card-metric-minimum' },
  ];
};

export const OpportunityCard = ({ project, basePath = '/projects' }) => {
  const navigate = useNavigate();
  const state = resolveCardState(project);
  const meta = CARD_STATES[state];
  const perf = project.performance;
  const typeMeta = projectTypeMeta(project.project_type, project.project_type_label);
  const TypeIcon = typeMeta.icon;
  const open = () => navigate(`${basePath}/${project.id}`);
  const metrics = metricsFor(state, project);
  // A real 0% raise is worth showing; an absent figure is not the same thing
  const hasProgress =
    project.funding_progress !== null &&
    project.funding_progress !== undefined &&
    Number.isFinite(Number(project.funding_progress));

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      className="group h-full flex flex-col text-left bg-white rounded-2xl overflow-hidden border border-black/[0.05] cursor-pointer
        transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.35)]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      data-testid={`opportunity-card-${project.code || project.id}`}
      data-state={state}
      aria-label={`${project.title}, ${project.location}`}
    >
      <div className="relative aspect-video bg-black/[0.05] overflow-hidden">
        {project.cover_image_url && (
          <img
            src={project.cover_image_url}
            alt={project.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
          />
        )}
        <div className="absolute inset-x-3 top-3 flex items-start justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium backdrop-blur-sm ${typeMeta.pill}`}
            data-testid="card-strategy-badge"
          >
            <TypeIcon size={13} weight="fill" />
            {typeMeta.label}
          </span>
          <span
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${meta.className}`}
            data-testid="card-status-badge"
          >
            {meta.label}
          </span>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-heading font-semibold tracking-tight leading-snug" data-testid="card-title">
          {project.title}
        </h3>
        <p className="flex items-center gap-1 text-xs text-foreground-muted mt-1" data-testid="card-location">
          <MapPin size={13} weight="fill" className="flex-shrink-0 opacity-70" />
          <span className="truncate">{project.location}</span>
        </p>
        {project.card_thesis && (
          <p
            className="text-sm text-foreground-muted leading-snug mt-2.5 line-clamp-2"
            data-testid="card-thesis"
          >
            {project.card_thesis}
          </p>
        )}

        <div className="h-px bg-black/[0.07] mt-4" />

        <div className="grid grid-cols-3 gap-3 mt-4">
          {metrics.map((m) => (
            <Metric key={m.label} value={m.value} label={m.label} testId={m.testId} verified={m.verified} />
          ))}
        </div>

        {state === 'exited' ? (
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="card-performance-variance">
            {perf?.projected_return_pct != null && (
              <p className="text-xs text-foreground-muted">
                vs {Number(perf.projected_return_pct).toFixed(1)}% projected
              </p>
            )}
            {perf?.variance_pp != null && (
              <span
                className={`text-xs font-medium ${perf.variance_pp >= 0 ? 'text-[rgb(34,150,94)]' : 'text-[rgb(200,60,60)]'}`}
              >
                {perf.variance_pp >= 0 ? '+' : ''}
                {perf.variance_pp} pp
              </span>
            )}
            {perf?.exit_date_label && (
              <p className="text-xs text-foreground-muted w-full">Exited {perf.exit_date_label}</p>
            )}
          </div>
        ) : state === 'coming_soon' ? (
          <p className="mt-5 text-xs text-foreground-muted" data-testid="card-launch-status">
            Preparing for launch · terms to be announced
          </p>
        ) : state === 'active' ? (
          <p className="mt-5 text-xs text-foreground-muted" data-testid="card-progress-status">
            Funded and underway · results reported as the project progresses
          </p>
        ) : hasProgress ? (
          <div className="mt-5">
            <FundingProgress progress={project.funding_progress} raised={project.funding_current} />
          </div>
        ) : (
          <p className="mt-5 text-xs text-foreground-muted" data-testid="card-funding-status">
            {state === 'fully_funded'
              ? 'Fully subscribed · no longer raising'
              : 'Raising now · funding progress coming soon'}
          </p>
        )}

        {state === 'open' ? (
          <span
            className="mt-auto pt-5 self-start"
            data-testid="card-invest-cta"
          >
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary text-white text-xs font-semibold tracking-tight animate-invest-pulse">
              {meta.cta}
              <ArrowRight size={13} weight="bold" />
            </span>
          </span>
        ) : (
          <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">
            {meta.cta}
            <ArrowRight size={15} weight="bold" />
          </span>
        )}
      </div>
    </article>
  );
};

export default OpportunityCard;
